/* eslint-disable jsx-a11y/alt-text */
import {
  UseFormRegister,
  RegisterOptions,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { FaSpinner } from "react-icons/fa";

type SelectOption = {
  id?: string | number;
  value: string | number;
  text: string;
  img?: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: string | any;
  setValue?: (val: string | any) => void;
  onChange?: (val: string | any) => void;
  clearErrors?: UseFormClearErrors<any>;
  formSetValue?: UseFormSetValue<any>;
  onSearch?: (query: string) => void; // 🔹 API search trigger
  isPending?: boolean; // 🔹 show spinner while loading
};

export default function SelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions,
  register,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value,
  setValue,
  onChange,
  clearErrors,
  formSetValue,
  onSearch,
  isPending,
}: Props) {
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // selected option text
  const selectedText =
    selectOptions?.find((opt) => opt?.value === value)?.text || "";

  // filter locally
  const filteredOptions = searchText
    ? selectOptions?.filter((opt) =>
      opt?.text?.toLowerCase().includes(searchText.toLowerCase())
    )
    : selectOptions;

  const handleSelect = (val: string | number) => {
    if (setValue) setValue(val);
    if (onChange) onChange(val);
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, val);
    setSelectOpen(false);
    setSearchText(""); // reset search after select
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdownToggle = () => {
    if (disabled) return;

    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < 250 && spaceAbove > 250);
    }

    setSelectOpen((prev) => !prev);
  };

  // 🔹 handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchText(query);

    if (onSearch && query.trim().length >= 3) {
      onSearch(query.trim());
    }
  };

  const labelNode = label && (
    <label
      htmlFor={name}
      className={`text-[#1E293B] text-sm ${labelClass} whitespace-nowrap`}
    >
      {label}
      {validation?.required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );

  const dropdown = (
    <div
      className={`absolute z-30 left-0 w-full rounded-xl bg-white ${searchText?.length >= 3 && "border border-gray-200"
        } shadow-lg max-h-60 overflow-y-auto ${openUpward ? "bottom-full mb-1" : "top-full mt-1"
        }`}
    >
      <ul>
        {filteredOptions?.map((opt, i) => (
          <li
            key={opt.id ?? i}
            className={`px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer flex items-center gap-2 ${opt.value === value ? "bg-blue-50 font-medium" : ""
              }`}
            onClick={() => handleSelect(opt.value)}
          >
            {opt?.img && <img src={opt.img} className="size-8 rounded-md object-cover" />}

            {opt.text}
          </li>
        ))}

        {/* Show "No results" only when user typed */}
        {searchText && filteredOptions?.length === 0 && (
          <li className="px-4 py-2 text-sm text-gray-400">No results found</li>
        )}
      </ul>
    </div>
  );

  const selectField = (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={`flex items-center justify-between px-4 py-2 text-sm rounded-full border ${error ? "border-red-500" : "border-gray-200"
          }  ${disabled ? "opacity-70 bg-gray-100" : "cursor-pointer bg-white"}`}
        onClick={handleDropdownToggle}
      >
        {/* 🔹 Inline search input */}
        <input
          type="text"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
          placeholder={placeholder}
          value={searchText?.length > 0 ? searchText : selectedText}
          onChange={handleSearchChange}
          autoComplete="new-password"
          onClick={(e) => {
            e.stopPropagation();
            setSelectOpen(true);
          }}
        />

        {/* 🔹 Spinner when searching */}
        {searchText?.length >= 3 && isPending && (
          <FaSpinner className="animate-spin text-gray-500 ml-2" size={16} />
        )}

        <IconChevronDown
          size={18}
          stroke={2.2}
          className={`text-gray-500 transition-transform ${selectOpen ? "rotate-180" : ""
            }`}
        />
      </div>

      {selectOpen && dropdown}

      {register && !formSetValue && (
        <input
          autoComplete="off"
          type="hidden"
          {...register(name, validation)}
          value={value ?? ""}
        />
      )}
    </div>
  );

  return (
    <div className={`space-y-1 relative ${className}`}>
      {labelNode}
      <div className="w-full">
        {removeDiv ? (
          selectField
        ) : (
          <div className="relative">{selectField}</div>
        )}
        <span className="text-red-500 text-xs block font-light">
          {error?.message || ""}
        </span>
      </div>
    </div>
  );
}
